/* eslint-disable */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import {
  CRYPTO_LOGOS,
  CRYPTO_APP_LOGOS,
  STABLECOIN_LOGOS,
  STABLECOIN_LOGOS_FULL,
  STABLECOIN_APP_LOGOS,
  STABLECOIN_APP_LOGOS_FULL,
  PAYMENT_METHOD_LOGOS,
} from '@/constants';
import {
  CartSummary as StyledCartSummary,
  SummaryRow,
  SubtotalLabel,
  TotalRow,
  EmailSection,
  EmailLabel,
  EmailInput,
  GiftMessageInput,
  ErrorMessage,
  EmailSavedAlert,
  CheckboxContainer,
  Checkbox,
  CheckboxLabel,
  CheckoutButton,
  ActionButton,
  LoadingSpinner,
  SectionTitle
} from './CartStyledComponents';
import { CartItemWithCard } from './useCartLogic';
import type { EmailCheck } from './useEmailCheck';
import { Card, CardLocale, getCardTitle } from '../../types/card';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { EmailIcon, PaymentIcon, ArrowBackIcon } from '../icons';
import { OtpStepUpModal, OtpChallenge } from './OtpStepUpModal';
import { KycStepUpModal, KycChallenge } from './KycStepUpModal';
import BalancePanel from './BalancePanel';
import PayerDetailsPanel from './PayerDetailsPanel';
import { useErrorMessage } from '../../hooks/useErrorMessage';
import styles from './Cart.module.css';
import type { CryptoPayOption, PaymentTile } from '../../types/order';

export type PaymentMethod = 'crypto' | 'bank_transfer';

interface CartSummaryProps {
  cartPricing: any;
  cartItems: CartItemWithCard[];
  selectedCurrency: string;
  couponCode: string;
  setCouponCode: (code: string) => void;
  isLoadingPricing: boolean;
  couponError: string;
  setCouponError: (error: string) => void;
  pricingError: string;
  minOrderError: { min: number; current: number } | null;
  email: string;
  emailError: string;
  emailSaved: boolean;
  // What the API says about the typed addresses: a domain that takes no
  // mail (no order possible), or a likely typo the shopper has to answer.
  emailCheck: EmailCheck;
  giftEmailCheck: EmailCheck;
  // The single required consent: confirms the country/region restrictions
  // check AND expressly requests immediate performance, which forfeits the
  // statutory 14-day withdrawal right (see Terms and Conditions, IV. Right
  // of Withdrawal). Never pre-ticked.
  expressConsent: boolean;
  setExpressConsent: (consented: boolean) => void;
  subscribeToNewsletter: boolean;
  setSubscribeToNewsletter: (subscribe: boolean) => void;
  buyAsGift: boolean;
  setBuyAsGift: (asGift: boolean) => void;
  giftEmail: string;
  giftEmailError: string;
  giftMessage: string;
  orderLoading: boolean;
  isFormValid: boolean;
  paymentMethod: PaymentMethod;
  setPaymentMethod: (method: PaymentMethod) => void;
  // Which of the two crypto tiles is selected. Only meaningful while
  // paymentMethod is 'crypto'; the server maps it to a provider.
  payOption: CryptoPayOption;
  setPayOption: (option: CryptoPayOption) => void;
  // Bank transfer (SEPA) is offered to logged-in users only; guests see the
  // tile disabled with a login hint. The backend enforces the same rule.
  isAuthenticated: boolean;
  // Products in the cart flagged as "account required" (deduped). Only
  // meaningful together with authRequiredBlocked, which is true when a guest
  // holds at least one of them - then no payment method is offered at all.
  authRequiredCards: Card[];
  authRequiredBlocked: boolean;
  // Products in the cart that block a payment method (deduped), and the
  // tiles they block between them.
  paymentRestrictedCards: Card[];
  blockedPaymentTiles: PaymentTile[];
  // Tiles an admin switched off for everyone: grayed out as currently
  // unavailable, or not rendered at all. Ready: the switches were read, so
  // the tiles can be drawn.
  unavailablePaymentTiles: PaymentTile[];
  hiddenPaymentTiles: PaymentTile[];
  paymentTilesReady: boolean;
  // No tile can be picked, whatever closed them. NeedsLogin: only bank
  // transfer is left and the guest has to log in for it. NoPaymentPossible:
  // no tile is left at all. In both the cart cannot be checked out as it is.
  paymentNeedsLogin: boolean;
  noPaymentPossible: boolean;
  // The last checkout attempt met an amount rule that needs an account
  // (guest on a crypto tile): a notice with the login button, not a decline.
  amountRuleLoginRequired: boolean;
  // balance
  userBalanceCents: number;
  isEmailVerified: boolean;
  balanceApplyCents: number;
  setBalanceApplyCents: (cents: number) => void;
  onApplyCoupon: () => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onGiftEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onGiftMessageChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onCheckout: () => void;
  // Cloudflare Turnstile widget. Null when not configured; rendered above
  // the Buy button when present. The isFormValid prop already accounts for
  // the token requirement, so we don't need to also gate the button here.
  turnstileWidget?: React.ReactNode;
  // Risk-band step-up (OTP) modal: non-null when the backend demanded a
  // fresh email/phone OTP or a verified phone before accepting the order.
  otpChallenge: OtpChallenge | null;
  onOtpClose: () => void;
  onOtpVerified: () => void;
  // Identity-verification (KYC) step-up modal: non-null when the backend
  // demanded an approved identity check for this band.
  kycChallenge: KycChallenge | null;
  onKycClose: () => void;
  onKycVerified: () => void;
  profileEmail?: string;
  profilePhone?: string;
  // SEPA payer details. The panel renders while bank transfer is selected
  // and an external residual remains; the selected account id rides on the
  // buy request (the server rejects SEPA orders without it).
  payerBankAccountId: string | null;
  setPayerBankAccountId: (id: string | null) => void;
  // Recoverable payer-gate error from useCartLogic (missing/stale account,
  // missing profile name) - shown at the panel, not on /order/declined.
  payerGateError: unknown;
  // Lifted to useCartLogic because the SEPA hard guard needs it; BalancePanel
  // still computes and reports it through onFullyCoveredChange.
  isFullyCoveredByBalance: boolean;
  onFullyCoveredChange: (covered: boolean) => void;
}

const CartSummaryTotals: React.FC<{ cartPricing: any; cartItems: CartItemWithCard[]; selectedCurrency: string; couponCode: string }> = ({
  cartPricing,
  cartItems,
  selectedCurrency,
  couponCode
}) => {
  const t = useTranslations('cart');

  if (!cartPricing) {
    const fallbackTotal = cartItems.reduce((total, item) => {
      return total + (item.selectedValue * item.quantity);
    }, 0);

    return (
      <>
        <SummaryRow>
          <SubtotalLabel>{t('summary.totals.subtotal')}</SubtotalLabel>
          <span>{selectedCurrency} {fallbackTotal.toFixed(2)}</span>
        </SummaryRow>
        <TotalRow>
          <span>{t('summary.totals.total')}</span>
          <span>{selectedCurrency} {fallbackTotal.toFixed(2)}</span>
        </TotalRow>
      </>
    );
  }

  return (
    <>
      <SummaryRow>
        <SubtotalLabel>{t('summary.totals.subtotal')}</SubtotalLabel>
        <span>{cartPricing.currency} {cartPricing.totalNoDiscount.toFixed(2)}</span>
      </SummaryRow>
      {cartPricing.savings > 0 && (
        <SummaryRow>
          <span className={styles.savingsText}>
            {t('summary.totals.savings')} {couponCode ? `(${couponCode})` : ''}
          </span>
          <span style={{ color: 'var(--color-success)', fontWeight: 700 }}>
            -{cartPricing.currency} {cartPricing.savings.toFixed(2)}
          </span>
        </SummaryRow>
      )}
      <TotalRow>
        <span>{t('summary.totals.total')}</span>
        <span>{cartPricing.currency} {cartPricing.total.toFixed(2)}</span>
      </TotalRow>
    </>
  );
};

// Under an email field: red when the domain takes no mail, amber while a
// likely typo waits for the shopper's answer. Each way out is one button
// named with the address it leaves in the field.
const EmailCheckNotice: React.FC<{ check: EmailCheck; typed: string }> = ({ check, typed }) => {
  const t = useTranslations('cart');

  if (!check.notFound && !check.needsAnswer) return null;

  const address = typed.trim();
  const domainOf = (value: string) => value.slice(value.lastIndexOf('@') + 1);

  return (
    <div
      className={`${styles.emailNotice} ${check.notFound ? styles.emailNoticeDanger : ''}`}
      role="alert"
    >
      <div className={styles.emailNoticeText}>
        {check.notFound
          ? t('summary.emailCheck.notFound', { domain: domainOf(address) })
          : t('summary.emailCheck.didYouMean', { domain: domainOf(check.suggestion ?? '') })}
      </div>
      <div className={styles.emailNoticeActions}>
        {check.suggestion && (
          <button type="button" className={styles.emailNoticeButton} onClick={check.accept}>
            {t('summary.emailCheck.use', { email: check.suggestion })}
          </button>
        )}
        {!check.notFound && (
          <button
            type="button"
            className={`${styles.emailNoticeButton} ${styles.emailNoticeButtonQuiet}`}
            onClick={check.keep}
          >
            {t('summary.emailCheck.keep', { email: address })}
          </button>
        )}
      </div>
    </div>
  );
};

export const CartSummaryComponent: React.FC<CartSummaryProps> = ({
  cartPricing,
  cartItems,
  selectedCurrency,
  couponCode,
  setCouponCode,
  isLoadingPricing,
  couponError,
  setCouponError,
  pricingError,
  minOrderError,
  email,
  emailError,
  emailSaved,
  emailCheck,
  giftEmailCheck,
  expressConsent,
  setExpressConsent,
  subscribeToNewsletter,
  setSubscribeToNewsletter,
  buyAsGift,
  setBuyAsGift,
  giftEmail,
  giftEmailError,
  giftMessage,
  orderLoading,
  isFormValid,
  paymentMethod,
  setPaymentMethod,
  payOption,
  setPayOption,
  isAuthenticated,
  authRequiredCards,
  authRequiredBlocked,
  paymentRestrictedCards,
  blockedPaymentTiles,
  unavailablePaymentTiles,
  hiddenPaymentTiles,
  paymentTilesReady,
  paymentNeedsLogin,
  noPaymentPossible,
  amountRuleLoginRequired,
  userBalanceCents,
  isEmailVerified,
  balanceApplyCents,
  setBalanceApplyCents,
  onApplyCoupon,
  onEmailChange,
  onGiftEmailChange,
  onGiftMessageChange,
  onCheckout,
  turnstileWidget,
  otpChallenge,
  onOtpClose,
  onOtpVerified,
  kycChallenge,
  onKycClose,
  onKycVerified,
  profileEmail,
  profilePhone,
  payerBankAccountId,
  setPayerBankAccountId,
  payerGateError,
  isFullyCoveredByBalance,
  onFullyCoveredChange,
}) => {
  const t = useTranslations('cart');
  const locale = useLocale() as CardLocale;
  const { localizeFullPath } = useLocalizedPath();
  const expressConsentRef = useRef<HTMLLabelElement>(null);
  const emailSectionRef = useRef<HTMLDivElement>(null);
  const giftEmailSectionRef = useRef<HTMLDivElement>(null);
  const [highlightExpressConsent, setHighlightExpressConsent] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [giftEmailTouched, setGiftEmailTouched] = useState(false);
  const [highlightEmail, setHighlightEmail] = useState(false);
  const [highlightGiftEmail, setHighlightGiftEmail] = useState(false);
  // An always-open coupon field is a checkout leak: it prompts people to go
  // hunting for a code they don't have and often not come back. Collapsed
  // behind a link, and auto-opened if a code is already in play so applied
  // coupons never hide.
  const [couponOpen, setCouponOpen] = useState(!!couponCode);

  const validateEmail = (emailValue: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

  const totalInOrderCurrency: number | undefined =
    cartPricing?.total ?? undefined;

  const payerSectionRef = useRef<HTMLDivElement>(null);
  const [highlightPayer, setHighlightPayer] = useState(false);
  const resolveError = useErrorMessage();
  const payerErrorMessage = payerGateError ? resolveError(payerGateError) : '';

  // A payer-gate error (from the click guard or a server rejection) pulls
  // the eye to the panel the same way the other blocking fields do. The
  // cleared branch must drop the highlight explicitly: clearing the error
  // mid-timer (picking an account) cancels the un-highlight timeout, which
  // would otherwise leave the red frame on forever.
  useEffect(() => {
    if (!payerGateError) {
      setHighlightPayer(false);
      return;
    }
    setHighlightPayer(true);
    payerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightPayer(false), 3500);
    return () => clearTimeout(timer);
  }, [payerGateError]);

  // A checkout stopped at an address (it takes no mail, or a likely typo is
  // not answered) pulls the eye to its field like the other blocking fields.
  // The stop comes from useCartLogic.handleCheckout or from the API itself.
  useEffect(() => {
    if (!emailCheck.timesStopped) return;
    setHighlightEmail(true);
    emailSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightEmail(false), 3500);
    return () => clearTimeout(timer);
  }, [emailCheck.timesStopped]);

  useEffect(() => {
    if (!giftEmailCheck.timesStopped) return;
    setHighlightGiftEmail(true);
    giftEmailSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHighlightGiftEmail(false), 3500);
    return () => clearTimeout(timer);
  }, [giftEmailCheck.timesStopped]);

  const handleCheckoutClick = () => {
    setEmailTouched(true);
    setGiftEmailTouched(true);

    if (!email || !validateEmail(email)) {
      setHighlightEmail(true);
      emailSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightEmail(false), 3500);
      return;
    }

    if (buyAsGift && (!giftEmail || !validateEmail(giftEmail))) {
      setHighlightGiftEmail(true);
      giftEmailSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightGiftEmail(false), 3500);
      return;
    }

    // SEPA needs a payer account picked before anything is submitted (and
    // before any OTP can be challenged). Mirrors the hard guard in
    // useCartLogic.handleCheckout; this one just adds the scroll+highlight.
    if (paymentMethod === 'bank_transfer' && !isFullyCoveredByBalance && !payerBankAccountId) {
      setHighlightPayer(true);
      payerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightPayer(false), 3500);
      return;
    }

    // Without this consent we may not start performance inside the 14-day
    // withdrawal period, so the order cannot be placed at all.
    if (!expressConsent) {
      setHighlightExpressConsent(true);
      expressConsentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightExpressConsent(false), 3500);
      return;
    }

    onCheckout();
  };

  // What the payment restriction notice says under the product names.
  const paymentRestrictionText = noPaymentPossible
    ? 'blocked'
    : paymentNeedsLogin
      ? 'loginRequired'
      : 'description';

  // No tile is left and no product is the reason: an admin switched tiles
  // off. The account-required banner, when shown, already covers every tile.
  const noTileLeftBySwitch =
    (paymentNeedsLogin || noPaymentPossible) &&
    paymentRestrictedCards.length === 0 &&
    !authRequiredBlocked;

  // Why a tile cannot be picked: an admin switched it off, the guest has to
  // log in first, or a product in the cart blocks it (payment restrictions).
  type TileLock = 'unavailable' | 'login' | 'restricted';

  const isTileBlocked = (tile: PaymentTile) => blockedPaymentTiles.includes(tile);
  const isTileUnavailable = (tile: PaymentTile) => unavailablePaymentTiles.includes(tile);
  const isTileHidden = (tile: PaymentTile) => hiddenPaymentTiles.includes(tile);
  // The cart cannot be paid with it, whatever the reason.
  const isTileClosed = (tile: PaymentTile) =>
    isTileBlocked(tile) || isTileUnavailable(tile) || isTileHidden(tile);

  // A tile that is switched off says so, also to a guest: no point in
  // logging in for it.
  const lockOf = (tile: PaymentTile, needsLogin: boolean): TileLock | undefined => {
    if (isTileUnavailable(tile)) return 'unavailable';
    if (needsLogin) return 'login';
    return isTileBlocked(tile) ? 'restricted' : undefined;
  };

  const lockLineKey = (lock: TileLock, loginKey: string) => {
    if (lock === 'login') return loginKey;
    return lock === 'unavailable'
      ? 'summary.payment.offline.tile'
      : 'summary.payment.restricted.unavailable';
  };

  const lockLine = (lock: TileLock | undefined, loginKey: string) =>
    lock && (
      <div className={styles.paymentMethodDesc}>
        🔒 {t(lockLineKey(lock, loginKey))}
      </div>
    );

  // The states of a tile share one shell, so the look cannot drift.
  const tileShell = (
    key: string,
    lock: TileLock | undefined,
    selected: boolean,
    onSelect: () => void,
    inner: React.ReactNode,
  ) => {
    if (lock === 'login') {
      return (
        <Link
          key={key}
          href={localizeFullPath('/login')}
          className={styles.paymentMethodOption}
          aria-disabled="true"
          style={{ opacity: 0.5, display: 'block', textDecoration: 'none', color: 'inherit' }}
        >
          {inner}
        </Link>
      );
    }
    if (lock === 'restricted' || lock === 'unavailable') {
      return (
        <div
          key={key}
          className={`${styles.paymentMethodOption} ${styles.paymentMethodOptionLocked}`}
          aria-disabled="true"
        >
          {inner}
        </div>
      );
    }
    return (
      <div
        key={key}
        className={`${styles.paymentMethodOption} ${selected ? styles.paymentMethodOptionActive : ''}`}
        onClick={onSelect}
      >
        {inner}
      </div>
    );
  };

  // A crypto tile (classic or stablecoin). The stablecoin tile is marketed
  // as USDT only; when the classic tile is closed (a product blocks it, or
  // an admin switched it off) it is the one crypto way to pay, so it shows
  // everything its provider takes.
  const cryptoTileInner = (option: CryptoPayOption, lock?: TileLock) => {
    const selected = !lock && paymentMethod === 'crypto' && payOption === option;
    const full = option === 'stablecoin' && !lock && isTileClosed('classic');
    const copyKey = option === 'classic' ? 'crypto' : full ? 'stablecoinFull' : 'stablecoin';
    const logos = option === 'classic' ? CRYPTO_LOGOS : full ? STABLECOIN_LOGOS_FULL : STABLECOIN_LOGOS;
    const appLogos = option === 'classic' ? CRYPTO_APP_LOGOS : full ? STABLECOIN_APP_LOGOS_FULL : STABLECOIN_APP_LOGOS;
    // A handful of coin circles shares one row with the wide app badges
    // (the USDT-only tile); a full coin grid pushes them to their own row.
    const appsInline = logos.length <= 3;
    const appBadges = appLogos.map((logo) => (
      <span key={logo.name} className={styles.paymentMethodLogoApp} title={logo.name}>
        <Image src={`/logos/${logo.file}`} alt={`${logo.name} logo`} width={102} height={22} loading="lazy" unoptimized />
      </span>
    ));
    return (
      <>
        <div className={styles.paymentMethodHeader}>
          <div className={`${styles.radioCircle} ${selected ? styles.radioCircleActive : ''}`}>
            {selected && <div className={styles.radioCircleDot} />}
          </div>
          <span className={styles.paymentMethodLabel}>
            {t(`summary.payment.${copyKey}.title`)}
            {/* t.has: English ships first; locales without the key just
                render the plain title, never a raw key path. */}
            {t.has(`summary.payment.${copyKey}.eta`) && (
              <span className={styles.paymentMethodEta}> ({t(`summary.payment.${copyKey}.eta`)})</span>
            )}
          </span>
        </div>
        <div className={styles.paymentMethodDesc}>{t(`summary.payment.${copyKey}.description`)}</div>
        {lockLine(lock, 'summary.payment.authRequired.loginRequired')}
        <div className={styles.paymentMethodLogos}>
          {logos.map((logo) => (
            <span key={logo.name} className={styles.paymentMethodLogoCrypto} title={logo.name}>
              <Image src={`/logos/${logo.file}`} alt={`${logo.name} logo`} width={32} height={32} loading="lazy" unoptimized />
            </span>
          ))}
          {appsInline && appBadges}
        </div>
        {!appsInline && appBadges.length > 0 && (
          <div className={styles.paymentMethodApps}>{appBadges}</div>
        )}
      </>
    );
  };

  // A guest holding an account-required product gets every tile as a link
  // to /login; that wins over a payment restriction.
  const cryptoTile = (option: CryptoPayOption) => {
    if (isTileHidden(option)) return null;
    const lock = lockOf(option, authRequiredBlocked);
    return tileShell(
      option,
      lock,
      paymentMethod === 'crypto' && payOption === option,
      () => {
        setPaymentMethod('crypto');
        setPayOption(option);
      },
      cryptoTileInner(option, lock),
    );
  };

  // SEPA is logged-in-only (wires are matched to accounts): a guest gets
  // the tile as a link to /login.
  const bankTile = () => {
    if (isTileHidden('bank_transfer')) return null;
    const lock = lockOf('bank_transfer', !isAuthenticated);
    const selected = !lock && paymentMethod === 'bank_transfer';
    return tileShell(
      'bank_transfer',
      lock,
      selected,
      () => setPaymentMethod('bank_transfer'),
      <>
        <div className={styles.paymentMethodHeader}>
          <div className={`${styles.radioCircle} ${selected ? styles.radioCircleActive : ''}`}>
            {selected && <div className={styles.radioCircleDot} />}
          </div>
          <span className={styles.paymentMethodLabel}>
            {t('summary.payment.bank.title')}
            {t.has('summary.payment.bank.eta') && (
              <span className={styles.paymentMethodEta}> ({t('summary.payment.bank.eta')})</span>
            )}
          </span>
        </div>
        <div className={styles.paymentMethodDesc}>{t('summary.payment.bank.description')}</div>
        {lockLine(lock, 'summary.payment.bank.loginRequired')}
        <div className={styles.paymentMethodLogos}>
          {PAYMENT_METHOD_LOGOS.filter((l) => l.name === 'SEPA').map((logo) => (
            <span key={logo.name} className={styles.paymentMethodLogoApp} title={logo.name}>
              <Image src={`/logos/${logo.file}`} alt={`${logo.name} logo`} width={57} height={22} loading="lazy" unoptimized />
            </span>
          ))}
        </div>
      </>,
    );
  };

  return (
    <StyledCartSummary>
      {(() => {
        console.log('💰 Rendering cart summary section');
        return null;
      })()}
      <SectionTitle>
        <PaymentIcon />
        {t('summary.title')}
      </SectionTitle>
      <EmailSection>
        {pricingError && (
          <div className={`${styles.warningBox} ${styles.warningBoxPricing}`}>
            ⚠️ {pricingError}
          </div>
        )}

        <div
          ref={emailSectionRef}
          className={`${styles.highlightSection} ${highlightEmail ? styles.highlightSectionActive : ''}`}
        >
          <EmailLabel required>
            <EmailIcon style={{ width: 18, height: 18 }} />
            {t('summary.email.label')}
          </EmailLabel>
          <EmailInput
            type="email"
            value={email}
            onChange={onEmailChange}
            onBlur={() => setEmailTouched(true)}
            placeholder={t('summary.email.placeholder')}
          />
          {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
          {emailTouched && !email && !emailError && (
            <ErrorMessage>{t('summary.email.required')}</ErrorMessage>
          )}
          {emailTouched && email && !emailError && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && (
            <ErrorMessage>{t('summary.email.invalid')}</ErrorMessage>
          )}
          <EmailCheckNotice check={emailCheck} typed={email} />
          {emailSaved && (
            <EmailSavedAlert>
              {t('summary.email.saved')}
            </EmailSavedAlert>
          )}
        </div>

        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            checked={buyAsGift}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBuyAsGift(e.target.checked)}
          />
          <CheckboxLabel>
            {t('summary.gift.buyAsGift')}
          </CheckboxLabel>
        </CheckboxContainer>

        {buyAsGift && (
          <div
            ref={giftEmailSectionRef}
            className={`${styles.highlightSection} ${highlightGiftEmail ? styles.highlightSectionActive : ''}`}
            style={{ marginTop: 16 }}
          >
            <EmailLabel required>
              {t('summary.gift.recipientEmail')}
            </EmailLabel>
            <EmailInput
              type="email"
              value={giftEmail}
              onChange={onGiftEmailChange}
              onBlur={() => setGiftEmailTouched(true)}
              placeholder={t('summary.gift.recipientPlaceholder')}
            />
            {giftEmailError && <ErrorMessage>{giftEmailError}</ErrorMessage>}
            {giftEmailTouched && !giftEmail && !giftEmailError && (
              <ErrorMessage>{t('summary.gift.recipientRequired')}</ErrorMessage>
            )}
            {giftEmailTouched && giftEmail && !giftEmailError && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(giftEmail) && (
              <ErrorMessage>{t('summary.gift.recipientInvalid')}</ErrorMessage>
            )}
            <EmailCheckNotice check={giftEmailCheck} typed={giftEmail} />

            <div style={{ marginTop: 16 }}>
              <EmailLabel>
                {t('summary.gift.messageLabel')}
              </EmailLabel>
              <GiftMessageInput
                value={giftMessage}
                onChange={onGiftMessageChange}
                placeholder={t('summary.gift.messagePlaceholder')}
                maxLength={500}
              />
              <div className={styles.charCount}>
                {giftMessage.length}/500 {t('summary.gift.characters')}
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 24 }}>
          {!couponOpen ? (
            <button
              type="button"
              className={styles.couponToggle}
              onClick={() => setCouponOpen(true)}
              aria-expanded={false}
            >
              {t('summary.coupon.label')}
            </button>
          ) : (
            <>
              <EmailLabel>
                {t('summary.coupon.label')}
              </EmailLabel>
              <div className={styles.couponRow}>
                <EmailInput
                  type="text"
                  value={couponCode}
                  autoFocus
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCouponCode(e.target.value.toUpperCase());
                    if (couponError) {
                      setCouponError('');
                    }
                  }}
                  placeholder={t('summary.coupon.placeholder')}
                  style={{ flex: 1 }}
                />
                <button
                  className={styles.couponApplyButton}
                  onClick={onApplyCoupon}
                  disabled={isLoadingPricing || !couponCode.trim()}
                >
                  {t('summary.coupon.apply')}
                </button>
              </div>
              {couponError && <ErrorMessage>{couponError}</ErrorMessage>}
              {cartPricing?.couponApplied && couponCode && (
                <div className={styles.couponApplied}>
                  ✓ {t('summary.coupon.applied', { code: couponCode })}
                </div>
              )}
            </>
          )}
        </div>

      </EmailSection>
      <CartSummaryTotals
        cartPricing={cartPricing}
        cartItems={cartItems}
        selectedCurrency={selectedCurrency}
        couponCode={couponCode}
      />
      <div style={{ marginTop: 24, marginBottom: 16 }}>
        <div className={styles.paymentMethodTitle}>
          💳 {t('summary.payment.title')}
        </div>

        {/* A guest holding an account-required product has no way to pay, so
            this one IS a blocking condition and earns a banner (unlike the
            country tick below). It names the products so the shopper can
            decide between logging in and removing them. */}
        {authRequiredBlocked && (
          <div className={styles.cartNotice}>
            <span className={styles.cartNoticeIcon} aria-hidden="true">🔐</span>
            <div className={styles.cartNoticeBody}>
              <div className={styles.cartNoticeTitle}>
                {t('summary.payment.authRequired.title')}
              </div>
              <ul className={styles.cartNoticeList}>
                {authRequiredCards.map((card) => (
                  <li key={card._id} className={styles.cartNoticeChip}>
                    {getCardTitle(card, locale)}
                  </li>
                ))}
              </ul>
              <div className={styles.cartNoticeText}>
                {t('summary.payment.authRequired.description')}
              </div>
              <Link href={localizeFullPath('/login')} className={styles.cartNoticeCta}>
                {t('summary.payment.authRequired.cta')}
              </Link>
            </div>
          </div>
        )}

        {/* Products that block a payment method. Not shown to a guest who has
            to log in first: the banner above already covers every tile. */}
        {paymentRestrictedCards.length > 0 && !authRequiredBlocked && (
          <div className={styles.cartNotice}>
            <span className={styles.cartNoticeIcon} aria-hidden="true">💳</span>
            <div className={styles.cartNoticeBody}>
              <div className={styles.cartNoticeTitle}>
                {t('summary.payment.restricted.title')}
              </div>
              <ul className={styles.cartNoticeList}>
                {paymentRestrictedCards.map((card) => (
                  <li key={card._id} className={styles.cartNoticeChip}>
                    {getCardTitle(card, locale)}
                  </li>
                ))}
              </ul>
              <div className={styles.cartNoticeText}>
                {t(`summary.payment.restricted.${paymentRestrictionText}`)}
              </div>
              {paymentNeedsLogin && (
                <Link href={localizeFullPath('/login')} className={styles.cartNoticeCta}>
                  {t('summary.payment.authRequired.cta')}
                </Link>
              )}
            </div>
          </div>
        )}

        {noTileLeftBySwitch && (
          <div className={styles.cartNotice}>
            <span className={styles.cartNoticeIcon} aria-hidden="true">💳</span>
            <div className={styles.cartNoticeBody}>
              <div className={styles.cartNoticeTitle}>
                {t('summary.payment.offline.title')}
              </div>
              <div className={styles.cartNoticeText}>
                {t(`summary.payment.offline.${paymentNeedsLogin ? 'loginRequired' : 'none'}`)}
              </div>
              {paymentNeedsLogin && (
                <Link href={localizeFullPath('/login')} className={styles.cartNoticeCta}>
                  {t('summary.payment.authRequired.cta')}
                </Link>
              )}
            </div>
          </div>
        )}

        {amountRuleLoginRequired && (
          <div className={styles.cartNotice}>
            <span className={styles.cartNoticeIcon} aria-hidden="true">🔐</span>
            <div className={styles.cartNoticeBody}>
              <div className={styles.cartNoticeTitle}>
                {t('summary.payment.amountRule.title')}
              </div>
              <div className={styles.cartNoticeText}>
                {t('summary.payment.amountRule.description')}
              </div>
              <Link href={localizeFullPath('/login')} className={styles.cartNoticeCta}>
                {t('summary.payment.authRequired.cta')}
              </Link>
            </div>
          </div>
        )}

        {paymentTilesReady && (
          <div className={styles.paymentMethodGrid}>
            {cryptoTile('classic')}
            {cryptoTile('stablecoin')}
            {bankTile()}
          </div>
        )}
      </div>

      {/* SEPA payer details directly under the tiles: the transfer must name
          its payer before checkout - and before any OTP challenge can fire.
          Hidden when balance covers everything (no wire will be sent) -
          UNLESS a payer-gate error is pending: the server clamps
          balanceApplyCents and can reintroduce a residual the FE thought was
          covered, and its rejection must never be invisible. */}
      {paymentMethod === 'bank_transfer' && isAuthenticated && (!isFullyCoveredByBalance || !!payerGateError) && (
        <div
          ref={payerSectionRef}
          className={`${styles.highlightSection} ${highlightPayer ? styles.highlightSectionActive : ''}`}
        >
          <PayerDetailsPanel
            selectedAccountId={payerBankAccountId}
            onSelectAccount={setPayerBankAccountId}
          />
          {payerErrorMessage && <ErrorMessage>{payerErrorMessage}</ErrorMessage>}
        </div>
      )}

      {/* Pay-with-balance sits directly under the payment method selector
          so the user reads it as part of the same "how am I paying?"
          decision, not buried near the checkout button. */}
      <BalancePanel
        userBalanceCents={userBalanceCents}
        isEmailVerified={isEmailVerified}
        balanceApplyCents={balanceApplyCents}
        setBalanceApplyCents={setBalanceApplyCents}
        selectedCurrency={selectedCurrency}
        paymentMethod={paymentMethod}
        totalInOrderCurrency={totalInOrderCurrency}
        onFullyCoveredChange={onFullyCoveredChange}
      />

      {/* The single required tick (country restrictions + withdrawal consent
          merged), kept before the optional newsletter opt-in. */}
      <CheckboxContainer ref={expressConsentRef} highlight={highlightExpressConsent}>
        <Checkbox
          type="checkbox"
          checked={expressConsent}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setExpressConsent(e.target.checked);
            if (e.target.checked) setHighlightExpressConsent(false);
          }}
        />
        <CheckboxLabel required>
          {t('summary.checkboxes.expressConsent')}
        </CheckboxLabel>
      </CheckboxContainer>

      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          checked={subscribeToNewsletter}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubscribeToNewsletter(e.target.checked)}
        />
        <CheckboxLabel>
          {t('summary.checkboxes.newsletter')}
        </CheckboxLabel>
      </CheckboxContainer>

      {/* Only genuinely blocking conditions get a red banner - the consent
          tick gets the scroll+highlight in handleCheckoutClick instead, so
          the cart never opens with a red error on it. */}
      {minOrderError && (
        <div className={`${styles.warningBox} ${styles.warningBoxDanger}`}>
          ⚠️ {t('summary.validation.minOrderUsd', { min: minOrderError.min, current: minOrderError.current.toFixed(2) })}
        </div>
      )}


      {turnstileWidget && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '0 0 16px' }}>
          {turnstileWidget}
        </div>
      )}

      <CheckoutButton
        onClick={handleCheckoutClick}
        disabled={orderLoading || !!pricingError || !!minOrderError || !isFormValid || authRequiredBlocked || paymentNeedsLogin || noPaymentPossible}
      >
        {orderLoading ? (
          <>
            {t('summary.checkout.processing')}
            <LoadingSpinner size={20} />
          </>
        ) : (
          <>
            <PaymentIcon style={{ width: 18, height: 18 }} />
            {isFullyCoveredByBalance ? t('summary.checkout.confirm') : t('summary.checkout.proceed')}
          </>
        )}
      </CheckoutButton>
      {/* Localized: a bare "/" dropped non-English shoppers out of their
          locale in the middle of checkout. Centred to sit under the checkout
          button, which is `margin: 0 auto` - left-aligned it read as an
          unrelated stray control. */}
      <Link href={localizeFullPath('/')} style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
        <ActionButton variant="outline">
          <ArrowBackIcon style={{ width: 18, height: 18 }} />
          {t('page.continueShopping')}
        </ActionButton>
      </Link>

      {otpChallenge && (
        <OtpStepUpModal
          challenge={otpChallenge}
          profileEmail={profileEmail}
          profilePhone={profilePhone}
          onClose={onOtpClose}
          onVerified={onOtpVerified}
        />
      )}
      {kycChallenge && (
        <KycStepUpModal
          challenge={kycChallenge}
          onClose={onKycClose}
          onVerified={onKycVerified}
        />
      )}
    </StyledCartSummary>
  );
};
